/**
 * /signup — account creation against auth.thriv.es.
 *
 * The account is created on the shared auth service and the response already
 * carries the session tokens, so the server sets the session cookies and the
 * user lands straight in Friday. Google sign-in creates the account on the
 * auth service as well, through the same flow the login page uses.
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { GoogleSignInButton } from '@/features/auth/GoogleSignInButton'
import { ApiError } from '@/lib/api'
import { FridayMark } from '@/components/branding/FridayMark'
import { useTheme } from '@/features/theme/ThemeProvider'

const PASSWORD_HINT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character. Maximum 72.'

function strongEnough(password: string): boolean {
  return (
    password.length >= 8 &&
    // The auth service hashes with bcrypt, which only reads the first 72 bytes.
    password.length <= 72 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /\d/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  )
}

export function SignupPage() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/signup' }) as { next?: string }
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The account exists, but the address has to be confirmed before it can be
  // used. The session is not established until then.
  const [pending, setPending] = useState<string | null>(null)
  const { theme } = useTheme()

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (!strongEnough(password)) {
      setError(PASSWORD_HINT)
      return
    }
    setBusy(true)
    try {
      const result = await register({ email, password, firstName, lastName }, remember)
      if (result.pendingVerification) {
        setPending(result.email)
        return
      }
      navigate({ to: search.next ?? '/w' })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not create the account'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  if (pending) {
    return (
      <div className="flex h-full items-center justify-center bg-app px-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <FridayMark size={64} className="mx-auto mb-3" />
            <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
            <p className="mt-1 text-sm text-fg-muted">One click and you are in.</p>
          </div>
          <div
            className="space-y-4 border border-border bg-elevated p-6 shadow-lg"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <p className="text-sm text-fg-muted">
              We sent a confirmation link to{' '}
              <span className="font-medium text-fg">{pending}</span>. Open it to activate your account.
            </p>
            <p className="text-xs text-fg-muted">
              The link works for 24 hours. Your account is created already — you only need to confirm the address
              before you can sign in.
            </p>
            <Link to="/login" search={{ next: search.next }} className="btn-primary w-full justify-center">
              Back to sign in
            </Link>
          </div>
          <p className="mt-4 text-center text-xs text-fg-muted">
            <span className="opacity-60">Theme · {theme.name}</span>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <FridayMark size={64} className="mx-auto mb-3" />
          <h1 className="text-2xl font-semibold tracking-tight">Create your Friday account</h1>
          <p className="mt-1 text-sm text-fg-muted">Start shipping with your team.</p>
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-3 border border-border bg-elevated p-6 shadow-lg"
          style={{ borderRadius: 'var(--radius-lg)' }}
        >
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-fg">First name</span>
              <input
                type="text"
                autoComplete="given-name"
                required
                maxLength={50}
                className="input mt-1"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-fg">Last name</span>
              <input
                type="text"
                autoComplete="family-name"
                required
                maxLength={50}
                className="input mt-1"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={busy}
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-fg">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-fg">Password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className="input mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            <span className="mt-1 block text-xs text-fg-muted">{PASSWORD_HINT}</span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-fg">Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className="input mt-1"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-fg-muted">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={busy}
            />
            <span>Remember me on this device</span>
          </label>
          {error && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
          <div className="flex items-center gap-3 text-xs text-fg-muted">
            <span className="h-px flex-1 bg-border" />
            <span>or</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <GoogleSignInButton next={search.next} remember={remember} label="Sign up with Google" />
        </form>
        <p className="mt-4 text-center text-xs text-fg-muted">
          Already have an account?{' '}
          <Link
            to="/login"
            search={{ next: search.next }}
            className="font-medium text-accent hover:underline"
          >
            Sign in
          </Link>
          <br />
          <span className="opacity-60">Theme · {theme.name}</span>
        </p>
      </div>
    </div>
  )
}
